import { useCallback, useEffect, useRef, useState } from 'react';
import { type Id, toast } from 'react-toastify';
import {
  useCreateAndStartBowtieCreationJobMutation,
  useGetBackgroundJobDetailsQuery,
} from '../api/enhanced/enhanced-v4-api';
import { useDeleteBowtieAndMoveFiltersMutation } from '../api/generated/bowtie-api';
import type { BowtieDto } from '../api/generated/v4-api';
import { useConfigurationContext } from '../context/configuration.context';
import { useDiagramContext } from '../context/diagram.context';
import { BOWTIE_TEMP_EF_BACKEND_CREATION_ENABLED } from '../helpers/constants';
import { updateToast } from '../helpers/toast-util';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  type DiagramDisplayFilterState,
  selectCauses,
  selectConsequences,
  selectEscalationFactors,
  selectFilter,
  setDiagramDisabled,
} from '../redux/slices/diagram';
import { JobStatus, setJobStatus, submitJob } from '../redux/slices/running-jobs';
import {
  createEscalationFactorsAfterJob,
  type DiagramSnapshot,
  type EscalationFactorPersistenceResult,
} from './create-escalation-factors-after-job';

/**
 * Custom hook for creating backend records from a Bowtie diagram.
 * Handles the creation process, status monitoring, and UI feedback.
 *
 * @param bowtieDto - Optional BowtieDto for flow diagram
 * @param mueLabel - Optional Material Unwanted Event label
 * @param mueRecordId - Optional Material Unwanted Event record ID
 *
 * @returns Object containing:
 * - handleClick: Function to trigger the backend records creation
 * - disabled: Boolean indicating if the creation process is in progress
 *
 * @remarks
 * This hook manages the entire lifecycle of creating backend records:
 * - Initiates the creation job
 * - Monitors job status through polling
 * - Handles success/failure scenarios
 * - Manages toast notifications
 * - Updates URL parameters on success
 * - Cleans up saved diagrams if necessary
 *
 * @example
 * ```typescript
 * const { handleClick, disabled } = useCreateBackendRecords(bowtieDto);
 * ```
 */
const useCreateBackendRecords = (bowtieDto?: BowtieDto, mueLabel?: string, mueRecordId?: number) => {
  const [jobId, setJobId] = useState<number>();

  const toastIdRef = useRef<Id | undefined>(undefined);
  const diagramSnapshotRef = useRef<DiagramSnapshot | undefined>(undefined);
  const dispatch = useAppDispatch();
  const { diagramId } = useDiagramContext();
  const diagramFilterData = useAppSelector(selectFilter);
  const causes = useAppSelector(selectCauses);
  const consequences = useAppSelector(selectConsequences);
  const escalationFactors = useAppSelector(selectEscalationFactors);
  const bowtieConfiguration = useConfigurationContext();

  const [postCreateRecordsJob] = useCreateAndStartBowtieCreationJobMutation();
  const [deleteBowtieAndMoveFilters] = useDeleteBowtieAndMoveFiltersMutation();

  const { data: { status, jobDetails } = {}, isError } = useGetBackgroundJobDetailsQuery(
    { id: jobId! },
    {
      skip: !jobId,
      pollingInterval: jobId ? 5000 : 0, // 5 seconds polling interval
    }
  );

  useEffect(() => {
    if ('FINISHED' === status && toastIdRef.current) {
      setJobId(undefined);

      // jobDetails is a free-form backend string; missing or unparseable → treat as failure.
      let parsedJobDetails: { recordId?: unknown } | undefined;
      if (jobDetails) {
        try {
          parsedJobDetails = JSON.parse(jobDetails);
        } catch {
          parsedJobDetails = undefined;
        }
      }

      const recordIdNum = parsedJobDetails?.recordId == null ? Number.NaN : Number(parsedJobDetails.recordId);
      if (!Number.isFinite(recordIdNum)) {
        // Missing / non-numeric / NaN — surface as failure (also avoids navigating to ?records=NaN).
        updateToast(toastIdRef.current, 'Unable to create diagram', 'error');
        dispatch(
          setJobStatus({
            id: jobId!,
            status: JobStatus.Failed,
            completedDate: new Date().toISOString(),
          })
        );
        dispatch(setDiagramDisabled(false));
        diagramSnapshotRef.current = undefined;
        return;
      }
      const recordId = String(recordIdNum);
      const params = new URLSearchParams({ records: recordId });

      const snapshot = diagramSnapshotRef.current;
      const shouldPersistEfs =
        BOWTIE_TEMP_EF_BACKEND_CREATION_ENABLED && snapshot && snapshot.escalationFactors.length > 0;

      const candidateEfCount = shouldPersistEfs
        ? snapshot!.escalationFactors.filter((ef) => ef.label && ef.controlNodeId).length
        : 0;

      const efPromise: Promise<EscalationFactorPersistenceResult | undefined> = shouldPersistEfs
        ? createEscalationFactorsAfterJob(dispatch, recordIdNum, bowtieConfiguration, snapshot!).catch(() => ({
            total: candidateEfCount,
            created: 0,
            partial: 0,
            skipped: 0,
            failed: candidateEfCount,
          }))
        : Promise.resolve(undefined);

      // Leave the diagram disabled until the EF post-step + cleanup finish — otherwise the user
      // could edit a draft that's about to be deleted by cleanupDiagramAndFilters.
      let cleanOutcome = true;
      efPromise
        .then(async (efResult) => {
          let filtersPersisted = true;
          try {
            await cleanupDiagramAndFilters(recordId, diagramId, diagramFilterData);
          } catch {
            filtersPersisted = false;
          }
          const efClean = !efResult || efResult.total === 0 || efResult.created === efResult.total;
          cleanOutcome = efClean && filtersPersisted;
          updateToast(
            toastIdRef.current!,
            buildSuccessMessage(efResult, filtersPersisted),
            cleanOutcome ? 'success' : 'warning'
          );
        })
        .finally(() => {
          diagramSnapshotRef.current = undefined;
          dispatch(setDiagramDisabled(false));
          dispatch(
            setJobStatus({
              id: jobId!,
              status: cleanOutcome ? JobStatus.Success : JobStatus.Warning,
              url: `${window.location.origin}${window.location.pathname}?${params}`,
              completedDate: new Date().toISOString(),
            })
          );

          // Full page reload clears the cache — no explicit resetApiState needed
          // (unlike useCloneBowtie which uses SPA navigation).
          setTimeout(
            () => window.location.replace(`${window.location.origin}${window.location.pathname}?${params}`),
            2500
          );
        });
    } else if (('STAGED' === status || isError) && toastIdRef.current) {
      updateToast(toastIdRef.current, 'Unable to create diagram', 'error');

      dispatch(
        setJobStatus({
          id: jobId!,
          status: JobStatus.Failed,
          completedDate: new Date().toISOString(),
        })
      );

      dispatch(setDiagramDisabled(false));

      setJobId(undefined);
    }
  }, [status, isError]);

  const cleanupDiagramAndFilters = useCallback(
    async (recordId: string, diagramId: string | null, diagramFilterData?: DiagramDisplayFilterState) => {
      if (diagramId) {
        return deleteBowtieAndMoveFilters({
          id: diagramId,
          recordId,
          diagramFilterDto: { displayFilter: { ...(diagramFilterData ?? {}) } },
        }).unwrap();
      } else {
        return Promise.reject('Unable to cleanup diagram and filters - missing diagram ID');
      }
    },
    []
  );

  const handleClick = useCallback(async () => {
    if (!mueLabel && !mueRecordId) {
      toast.error('Please specify the Material Unwanted Event', { autoClose: 2000 });
      return;
    }

    toastIdRef.current = toast.loading('Creating backend records...');

    // Snapshot the diagram state at click time so the post-job EF/EFB creation step
    // doesn't get tripped up by redux being reset before navigation completes.
    diagramSnapshotRef.current = {
      causes: causes.map((cause) => ({
        id: cause.id,
        label: cause.label,
        controls: cause.controls.map((control) => ({ id: control.id, label: control.label })),
      })),
      consequences: consequences.map((consequence) => ({
        id: consequence.id,
        label: consequence.label,
        controls: consequence.controls.map((control) => ({ id: control.id, label: control.label })),
      })),
      escalationFactors: (escalationFactors ?? []).map((ef) => ({
        id: ef.id,
        label: ef.label,
        controlNodeId: ef.controlNodeId,
        barriers: (ef.barriers ?? []).map((barrier) => ({ id: barrier.id, label: barrier.label })),
      })),
    };

    // post records creation job
    const payload = { bowtieDto: bowtieDto! };

    try {
      const { id } = (await postCreateRecordsJob(payload).unwrap()) ?? {};

      setJobId(id);
      if (id) {
        dispatch(submitJob({ name: bowtieDto?.riskScenario, id: id! }));

        dispatch(setDiagramDisabled(true));
      }
    } catch {
      updateToast(toastIdRef.current, 'Unable to create diagram', 'error');
    }
  }, [mueLabel, mueRecordId, bowtieDto, causes, consequences, escalationFactors]);

  return {
    handleClick,
    disabled: Boolean(jobId),
  };
};

export default useCreateBackendRecords;

const buildSuccessMessage = (
  efResult: EscalationFactorPersistenceResult | undefined,
  filtersPersisted: boolean
): string => {
  const efClean = !efResult || efResult.total === 0 || efResult.created === efResult.total;

  if (efClean && filtersPersisted) return 'Diagram created successfully';
  if (efClean && !filtersPersisted) {
    return 'Diagram created successfully, though filters could not be persisted';
  }

  const missing = efResult!.total - efResult!.created;
  const efMessage = `${missing} escalation factor${missing === 1 ? '' : 's'} could not be fully saved`;
  if (filtersPersisted) {
    return `Diagram created, but ${efMessage}. Please review and add the missing items manually.`;
  }
  return `Diagram created, but ${efMessage} and filters could not be persisted. Please review and add the missing items manually.`;
};
